import React, { useEffect, useMemo, useRef, useState } from 'react';
import { chatGameApi } from './chatGameApi';
import WhodunnitCaseFile from './WhodunnitCaseFile';
import EvidenceMinigame from './EvidenceMinigame';
import soundManager from '../../utils/SoundManager';
import useViewport, { BP_NARROW } from '../../hooks/useViewport';

/**
 * InvestigationStage — Whodunnit STAGE 1: COLLECT CLUES (spec Phase C).
 *
 * Full-bleed 2D point-and-click over the game's scene backdrop: witness hotspots open
 * a 1:1 question thread (server-budgeted), clue hotspots inspect + file evidence to
 * the case file, the dock strip owns the timer + I'M READY. Deliberately no avatar
 * movement in v1 — this is a point-and-click, not a walking sim.
 *
 * Everything is driven by status.investigation (server truth). Optimistic bits are
 * local-only: pending witness question, just-found clue flash. Cinematics (guilty
 * reveal etc.) render ABOVE this layer untouched (zIndex 300000 vs our 1200).
 *
 * Responsive by container: hotspots are percent-positioned; the witness thread is a
 * bottom sheet capped at 560px; everything folds on narrow screens. One width gate:
 * ≥1100px (matchMedia, `wide`) docks the case file as a right sidebar; below, modal.
 */

const NOIR_BG = 'rgba(8,8,12,0.92)';
const MANILA = '#d9ba72';
const INK = '#e8ddc8';
const INK_DIM = 'rgba(232,221,200,0.6)';
const RED = '#ef4444';
const GREEN = '#4ade80';

export default function InvestigationStage({
    investigation,          // status.investigation block
    backgroundUrl = null,   // status.ui_theme.backgroundImage.url
    gameId,
    playerId,
    isSpectator = false,
    caseFileUnseen = 0,
    caseFileProps = null,   // full WhodunnitCaseFile prop bag — the stage hosts its own
                            // copy of the drawer (the chat panel's lives at zIndex 40,
                            // under this overlay). Same component, same storage keys.
    sceneAmbient = null,    // GameStepPage's caseAmbient: 'rain' only when the scene
                            // fiction has weather (Nina's 07-29 dry-pack lock) — the
                            // visual rain follows the SAME gate as the audio bed.
    isBotMode = false,      // bots play the stage too — all immersion theatrics off
                            // (matches the !isBotMode entrance-beat convention) and
                            // fresh bags skip the mini-game (instant reveal).
}) {
    const [showFile, setShowFile] = useState(false);
    const [focusWitness, setFocusWitness] = useState(null); // witness id
    const [focusClue, setFocusClue] = useState(null);       // {id, object, emoji, hint, evidence_line?, phase, fresh}
    // 🕹️ EVIDENCE MINI-GAMES (immersion batch item 6): a fresh bag plays a tiny
    // pointer toy (dust/uv/trace) in the inspect modal; the reveal waits on BOTH
    // the game and the API line. Texture only — the move is spent at tap, the
    // game always completes (skip link + backstop), re-reads stay instant.
    const [minigameDone, setMinigameDone] = useState(false);
    const [question, setQuestion] = useState('');
    const [pendingQ, setPendingQ] = useState(null);         // optimistic {q} while waiting
    const [localThreads, setLocalThreads] = useState({});   // witnessId -> [{q,a}] (optimistic, merged under server thread)
    const [localFound, setLocalFound] = useState({});       // clueId -> evidence_line (before next poll)
    const [readySent, setReadySent] = useState(false);
    const [errorFlash, setErrorFlash] = useState(null);
    const [now, setNow] = useState(Date.now());
    // 🗂 Desktop case-file dock (Nina 07-26): ≥1100px the file is a default-open right
    // sidebar instead of a tap-to-open modal; mobile keeps the modal. The stage is
    // full-bleed fixed, so the viewport IS the container — round 5 (08-03): derived
    // from the shared useViewport store (was a private matchMedia listener).
    const { width: vpWidth, isMobile: vpIsMobile } = useViewport();
    const wide = vpWidth >= BP_NARROW;
    const [fileDocked, setFileDocked] = useState(true);
    const threadEndRef = useRef(null);

    // 📱 Keyboard lift for the witness sheet (Nina's mini, 07-26 — the interaction
    // right AFTER the clue circles). The sheet is bottom-anchored inside a
    // position:fixed takeover, so iOS pins it to the layout viewport and the
    // keyboard covers the input AND the ASK button outright, with nothing to
    // scroll. Same visualViewport approach the chat dock uses; 0 when closed.
    const [kbLift, setKbLift] = useState(0);
    useEffect(() => {
        const vv = typeof window !== 'undefined' && window.visualViewport;
        if (!vv) return undefined;
        const update = () => setKbLift(Math.max(0, window.innerHeight - (vv.height + vv.offsetTop)));
        vv.addEventListener('resize', update);
        vv.addEventListener('scroll', update);
        update();
        return () => {
            vv.removeEventListener('resize', update);
            vv.removeEventListener('scroll', update);
        };
    }, []);

    // Going wide: the dock takes over from the modal (showFile starts false,
    // so the mount firing is a no-op).
    useEffect(() => {
        if (wide) setShowFile(false);
    }, [wide]);

    const inv = investigation || {};
    const witnesses = inv.witnesses || [];
    const clues = inv.clues || [];
    const youReady = inv.you_ready || readySent;

    // 🎬 IMMERSION THEATRICS (batch items 1-4): rain+lightning, Ken Burns drift,
    // witness silhouettes+murmur, polaroid bagging flight. Presentation only.
    const theatrics = !isBotMode;
    const rainOn = theatrics && sceneAmbient === 'rain';

    // Server-anchored countdown (started_at is server time; good enough at 1s grain)
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const remaining = useMemo(() => {
        if (!inv.started_at) return inv.duration || 180;
        const end = new Date(inv.started_at).getTime() + (inv.duration || 180) * 1000;
        return Math.max(0, Math.round((end - now) / 1000));
    }, [inv.started_at, inv.duration, now]);
    const mm = Math.floor(remaining / 60);
    const ss = String(remaining % 60).padStart(2, '0');

    // Reset optimistic state when a new investigation round opens
    const roundKey = inv.round || 1;
    const prevRoundRef = useRef(roundKey);
    useEffect(() => {
        if (prevRoundRef.current !== roundKey) {
            prevRoundRef.current = roundKey;
            setLocalThreads({}); setLocalFound({}); setReadySent(false);
            setFocusWitness(null); setFocusClue(null); setPendingQ(null);
            setMinigameDone(false);
        }
    }, [roundKey]);

    const flashError = (msg) => {
        setErrorFlash(msg);
        setTimeout(() => setErrorFlash(null), 3500);
    };

    const activeWitness = witnesses.find(w => w.id === focusWitness) || null;
    const serverThread = activeWitness?.thread || [];
    const extraThread = (localThreads[focusWitness] || []).slice(serverThread.length);
    const thread = [...serverThread, ...extraThread];
    const questionsLeft = activeWitness
        ? Math.max(0, (activeWitness.questions_left ?? 3) - extraThread.length)
        : 0;

    useEffect(() => {
        threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [thread.length, pendingQ]);

    const askWitness = async () => {
        const q = question.trim();
        if (!q || !activeWitness || pendingQ || questionsLeft <= 0 || isSpectator) return;
        if (outOfMoves) { flashError("You're out of moves tonight. Make your read."); return; }
        setPendingQ({ q });
        setQuestion('');
        try {
            const res = await chatGameApi.investigationAction(gameId, playerId, {
                op: 'witness', witness_id: activeWitness.id, question: q
            });
            setLocalThreads(prev => ({
                ...prev,
                [activeWitness.id]: [...(prev[activeWitness.id] || serverThread), { q, a: res.reply }]
            }));
        } catch (e) {
            flashError(e.message);
            setQuestion(q); // give the question back
        }
        setPendingQ(null);
    };

    const inspectClue = async (clue) => {
        if (isSpectator) return;
        const known = clue.evidence_line || localFound[clue.id];
        // Re-reading bagged evidence stays free; only a FRESH bag costs a move.
        if (!known && outOfMoves) { flashError("You're out of moves tonight. Make your read."); return; }
        setFocusClue({ ...clue, phase: known ? 'revealed' : 'inspecting', evidence_line: known || null, fresh: !known });
        if (known) return;
        setMinigameDone(!!isBotMode); // bots skip the toy: reveal as soon as the line lands
        try {
            const res = await chatGameApi.investigationAction(gameId, playerId, { op: 'clue', clue_id: clue.id });
            setLocalFound(prev => ({ ...prev, [clue.id]: res.evidence_line }));
            // The reveal gates on minigameDone too (render-derived), so the line
            // landing mid-game just waits for the player to finish their rub.
            setFocusClue(fc => (fc && fc.id === clue.id)
                ? { ...fc, evidence_line: res.evidence_line }
                : fc);
        } catch (e) {
            setFocusClue(null);
            flashError(e.message);
        }
    };

    const sendReady = async () => {
        if (youReady || isSpectator) return;
        setReadySent(true);
        try {
            await chatGameApi.investigationAction(gameId, playerId, { op: 'ready' });
        } catch (e) {
            setReadySent(false);
            flashError(e.message);
        }
    };

    const foundCount = clues.filter(c => c.found || localFound[c.id]).length;

    // 🎚️ Nightly move cap (Nina 07-26): server truth is inv.moves_left; the optimistic
    // spend derives from the SAME optimistic state the threads/clues already keep, so
    // it self-reconciles as polls catch up (no separate counter to drift).
    const optimisticSpend = Object.entries(localThreads).reduce((n, [wid, arr]) => {
        const serverLen = witnesses.find(x => x.id === wid)?.thread?.length || 0;
        return n + Math.max(0, arr.length - serverLen);
    }, 0) + clues.filter(c => !c.found && localFound[c.id]).length;
    const movesLeft = (inv.moves_left === null || inv.moves_left === undefined)
        ? null : Math.max(0, inv.moves_left - optimisticSpend);
    const outOfMoves = movesLeft !== null && movesLeft <= 0;

    const sidebarOpen = wide && !!caseFileProps && fileDocked;

    // Fresh-bag reveal gates on BOTH the mini-game and the evidence line
    // (hoisted from the modal so the polaroid flight can watch it too).
    const clueRevealed = !!focusClue
        && (focusClue.phase === 'revealed' || (minigameDone && !!focusClue.evidence_line));

    // 📸→🗂 Polaroid bagging beat: after a fresh reveal, a tilted photo of the
    // clue flies from the modal into the CASE FILE corner. Once per clue.
    const [bagFlight, setBagFlight] = useState(null); // {emoji, key}
    const flownRef = useRef({});
    useEffect(() => {
        if (!theatrics || !clueRevealed || !focusClue?.fresh) return undefined;
        const id = focusClue.id;
        if (flownRef.current[id]) return undefined;
        flownRef.current[id] = true;
        soundManager.play('cameraShutter'); // the photo IS the white flash
        // Let the shutter flash + FILED stamp land first, then fly
        const t = setTimeout(() => {
            soundManager.play('brushRub'); // paper whoosh under the flight
            setBagFlight({ emoji: focusClue.emoji || '🔍', key: id });
        }, 700);
        return () => clearTimeout(t);
    }, [theatrics, clueRevealed, focusClue]);

    // 🗣 Witness murmur: every few seconds one witness mutters a wordless "…"
    // bubble (visual half only — stage audio is the diegetic-sounds item).
    // Talked-out witnesses (🤐) stay silent, which reads as a quiet beat.
    const [murmur, setMurmur] = useState({ idx: -1, tick: 0 });
    useEffect(() => {
        if (!theatrics) return undefined;
        const t = setInterval(() => setMurmur(m => ({ idx: m.idx + 1, tick: m.tick + 1 })), 6500);
        return () => clearInterval(t);
    }, [theatrics]);
    const murmurIdx = (theatrics && witnesses.length && murmur.idx >= 0)
        ? murmur.idx % witnesses.length : -1;

    // ⛈🔊 Thunder synced to the CSS lightning: flashA peaks at 89% of its 13s
    // cycle (3s delay), flashB at 95% of 23s (9s delay). Thunder rides every
    // OTHER flash (26s/46s periods) + a 400ms distance lag — a storm outside,
    // not a strobe drummer. Timers die with the rain gate.
    useEffect(() => {
        if (!rainOn) return undefined;
        const timers = [];
        const arm = (firstMs, periodMs) => {
            timers.push(setTimeout(() => {
                soundManager.play('thunder');
                timers.push(setInterval(() => soundManager.play('thunder'), periodMs));
            }, firstMs));
        };
        arm(14970, 26000);
        arm(31250, 46000);
        return () => timers.forEach(x => { clearTimeout(x); clearInterval(x); });
    }, [rainOn]);

    const hotspotBase = {
        position: 'absolute', transform: 'translate(-50%, -50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        background: 'transparent', border: 'none', cursor: 'pointer', padding: 6,
        zIndex: 2,
        // Nina 07-26 desktop: press-drag on the emoji face started a native
        // content drag (Safari especially) that ate the click — "it just drags
        // the circle". Kill selection + drag on the hotspot and everything in it.
        userSelect: 'none', WebkitUserSelect: 'none', WebkitUserDrag: 'none',
        touchAction: 'manipulation',
    };

    // 🏷️ Edge clamp (Nina 07-31, from the @320 probe): labels center under
    // their circle, so an AI-placed hotspot near either screen edge pushed its
    // label past the viewport and the scene's overflow:hidden sheared it
    // ("…the fireplace in the Faculty L"). Near an edge the label nudges
    // inward; the CIRCLE never moves (it is the tap target at x,y).
    const edgeLabelShift = (x) => (
        x <= 12 ? { transform: 'translateX(35%)' }
            : x >= 88 ? { transform: 'translateX(-35%)' }
                : null
    );
    // 📱 Clue HINTS hide below 768 and surface in the inspect modal instead
    // (Nina 07-31 pick (b) over offset-staggering: positions are AI-rolled per
    // scenario, so collisions can't be styled away — hiding is the only option
    // that cannot fail). Witness NAMES always render: who's-who is
    // decision-critical, flavor hints are not.
    const narrowStage = vpIsMobile;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1200,
            background: `#0a0a0f`,
            display: 'flex', flexDirection: 'column',
        }}>
            <style>{`
                @keyframes invGlint { 0%,100% { box-shadow: 0 0 6px 2px rgba(217,186,114,0.25); } 50% { box-shadow: 0 0 14px 5px rgba(217,186,114,0.55); } }
                @keyframes invPulse { 0%,100% { transform: translate(-50%,-50%) scale(1); } 50% { transform: translate(-50%,-50%) scale(1.06); } }
                @keyframes invShutter { 0% { opacity: 0; } 15% { opacity: 1; } 100% { opacity: 0; } }
                @keyframes invSlideUp { from { transform: translateY(24px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                @keyframes invReadyPulse { 0%,100% { box-shadow: 0 0 10px 2px rgba(217,186,114,0.45); transform: scale(1); } 50% { box-shadow: 0 0 26px 9px rgba(217,186,114,0.85); transform: scale(1.045); } }
                /* 🎬 Immersion theatrics (batch items 1-4) */
                @keyframes invKenBurns { from { transform: scale(1.03) translate(0.6%, 0.4%); } to { transform: scale(1.09) translate(-1.2%, -0.8%); } }
                @keyframes invRainFall { to { transform: translateY(118vh); } }
                @keyframes invFlashA { 0%, 88%, 100% { opacity: 0; } 89% { opacity: 0.30; } 90.5% { opacity: 0.05; } 92% { opacity: 0.22; } 94% { opacity: 0; } }
                @keyframes invFlashB { 0%, 94%, 100% { opacity: 0; } 95% { opacity: 0.24; } 96.5% { opacity: 0; } 97.5% { opacity: 0.14; } 99% { opacity: 0; } }
                @keyframes invSway { 0%, 100% { transform: translateX(-50%) rotate(-2.5deg); } 50% { transform: translateX(-50%) rotate(2.5deg); } }
                @keyframes invMurmur { 0% { opacity: 0; transform: translate(-50%, 4px); } 18% { opacity: 0.95; } 70% { opacity: 0.85; } 100% { opacity: 0; transform: translate(-50%, -12px); } }
                @keyframes invPolaroidFly {
                    0% { left: 50%; top: 42%; transform: translate(-50%, -50%) scale(1) rotate(-7deg); opacity: 0; }
                    14% { opacity: 1; }
                    82% { opacity: 1; }
                    100% { left: calc(100% - 74px); top: 26px; transform: translate(-50%, -50%) scale(0.3) rotate(11deg); opacity: 0.2; }
                }
                /* -webkit-user-drag does not inherit — the emoji/label spans need it
                   directly or Safari still ghost-drags the glyph out of the button. */
                .inv-hotspots button, .inv-hotspots button * {
                    -webkit-user-drag: none; user-select: none; -webkit-user-select: none;
                }
            `}</style>

            {/* Scene backdrop + noir vignette. Ken Burns drift (theatrics): scale
                stays ≥1.03 so the pan never exposes an edge; hotspots live in
                their own layer, so their tap targets never move. */}
            <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : 'linear-gradient(135deg, #05060a 0%, #4e342e 100%)',
                backgroundSize: 'cover', backgroundPosition: 'center',
                filter: 'saturate(0.7) brightness(0.85)',
                animation: theatrics ? 'invKenBurns 46s ease-in-out infinite alternate' : 'none',
            }} />
            <div style={{
                position: 'absolute', inset: 0,
                background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.75) 100%)',
            }} />

            {/* ⛈ Rain + lightning — same dry-pack gate as the audio bed (rain only
                when the fiction has weather). z1: over the scene, under every UI
                layer (hotspots z2, strips z3/z4), so the desk stays dry paper. */}
            {rainOn && (
                <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', overflow: 'hidden' }}>
                    {Array.from({ length: 22 }).map((_, i) => (
                        <span key={`inv-rain-${i}`} style={{
                            position: 'absolute', top: -140, left: `${(i * 89 + 7) % 100}%`,
                            width: 1, height: 70 + (i % 5) * 16,
                            background: 'linear-gradient(to bottom, transparent, rgba(190,215,255,0.34))',
                            opacity: 0.45,
                            animation: `invRainFall ${(0.85 + (i % 5) * 0.18).toFixed(2)}s linear ${((i % 7) * 0.29).toFixed(2)}s infinite`,
                        }} />
                    ))}
                    <div style={{ position: 'absolute', inset: 0, background: '#dfe8ff', opacity: 0, mixBlendMode: 'screen', animation: 'invFlashA 13s linear 3s infinite' }} />
                    <div style={{ position: 'absolute', inset: 0, background: '#cfd8ef', opacity: 0, mixBlendMode: 'screen', animation: 'invFlashB 23s linear 9s infinite' }} />
                </div>
            )}

            {/* Top strip: stage title + timer + case file. Two-level left block
                (Nina 07-31, from the @320 probe): the old single flexWrap row put
                FOUR unlike things side by side and wrapped to THREE rows (~92px,
                ~16% of a small phone) — title over a combined meta line never
                wraps at any width. "STAGE 1:" dropped: the act name is the
                information, the stage number is implementation detail. */}
            <div style={{
                position: 'relative', zIndex: 3, display: 'flex', flexDirection: 'column', gap: 2,
                padding: 'max(8px, env(safe-area-inset-top)) 12px 8px',
                background: 'linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.25))',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        flex: 1, minWidth: 0,
                        // 0.06em + no emoji: pack fonts (typewriter) are wide, and
                        // "🔍 COLLECT CLUES" at 0.08em still clipped to "CLU…" @320.
                        color: MANILA, fontWeight: 900, letterSpacing: '0.06em', fontSize: 'clamp(12px, 2.4vmin, 16px)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                        COLLECT CLUES
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                        <div style={{
                            color: remaining <= 30 ? RED : INK, fontWeight: 900, fontVariantNumeric: 'tabular-nums',
                            fontSize: 'clamp(15px, 3vmin, 20px)', letterSpacing: '0.06em',
                        }}>⏱ {mm}:{ss}</div>
                    {caseFileProps && (
                        <button onClick={() => (wide ? setFileDocked(v => !v) : setShowFile(true))} style={{
                            position: 'relative', background: 'rgba(217,186,114,0.14)', color: MANILA,
                            border: `1px solid ${MANILA}55`, borderRadius: 8, padding: '5px 10px',
                            fontWeight: 800, cursor: 'pointer', fontSize: 'clamp(11px, 2.2vmin, 14px)',
                        }}>
                            🗂 CASE FILE
                            {caseFileUnseen > 0 && (
                                <span style={{
                                    position: 'absolute', top: -6, right: -6, background: RED, color: '#fff',
                                    borderRadius: 999, minWidth: 16, height: 16, fontSize: 10, fontWeight: 900,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px',
                                }}>{caseFileUnseen}</span>
                            )}
                        </button>
                    )}
                    </div>
                </div>
                {/* Meta row: full width so nothing ellipsizes at 320 (row-A
                    sharing left ~100px produced "0/4 bagg…") */}
                <div style={{ color: INK_DIM, fontSize: 'clamp(10px, 2vmin, 13px)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    Round {inv.round || 1} · {foundCount}/{clues.length} bagged
                    {movesLeft !== null && (
                        <span style={{ color: outOfMoves ? RED : MANILA, fontWeight: 800 }}>
                            {' '}· 🥾 {movesLeft} {movesLeft === 1 ? 'move' : 'moves'} left tonight
                        </span>
                    )}
                </div>
            </div>

            {/* Scene row: hotspots + (desktop ≥1100px) the docked case file */}
            <div style={{ position: 'relative', flex: 1, zIndex: 2, display: 'flex', minHeight: 0 }}>
            <div className="inv-hotspots" style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
                {witnesses.map((w, wi) => (
                    <button key={w.id} onClick={() => { setFocusClue(null); setFocusWitness(w.id); }}
                        draggable={false} onDragStart={e => e.preventDefault()}
                        style={{ ...hotspotBase, left: `${w.x}%`, top: `${w.y}%`, animation: 'invPulse 2.6s ease-in-out infinite' }}>
                        {/* 🕴 Silhouette (theatrics): a blurred swaying figure under the
                            head circle, so witnesses read as PEOPLE standing in the
                            room, not floating buttons. Behind the button's content
                            (zIndex -1 in its stacking context), never a tap target. */}
                        {theatrics && (
                            <span aria-hidden style={{
                                position: 'absolute', left: '50%', top: '44%', zIndex: -1,
                                width: 54, height: 96, pointerEvents: 'none',
                                transformOrigin: 'top center', transform: 'translateX(-50%)',
                                animation: `invSway ${(5 + (wi % 3) * 1.3).toFixed(1)}s ease-in-out infinite`,
                                background: 'linear-gradient(180deg, rgba(5,6,10,0.55), rgba(5,6,10,0.8) 45%, rgba(5,6,10,0.28) 92%, transparent)',
                                borderRadius: '46% 46% 40% 40% / 30% 30% 55% 55%',
                                filter: 'blur(5px)',
                            }} />
                        )}
                        {murmurIdx === wi && w.questions_left !== 0 && (
                            <span key={`murmur-${murmur.tick}`} aria-hidden style={{
                                position: 'absolute', left: '50%', bottom: '100%', marginBottom: 2,
                                transform: 'translateX(-50%)', pointerEvents: 'none',
                                background: 'rgba(0,0,0,0.6)', borderRadius: 8, padding: '0 7px',
                                color: INK, fontStyle: 'italic', fontWeight: 700,
                                fontSize: 15, letterSpacing: '0.2em',
                                animation: 'invMurmur 2.8s ease-out forwards', opacity: 0,
                            }}>…</span>
                        )}
                        <span style={{
                            width: 'clamp(44px, 8vmin, 60px)', height: 'clamp(44px, 8vmin, 60px)', borderRadius: '50%',
                            background: 'rgba(10,10,16,0.78)', border: `2px solid ${MANILA}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 'clamp(20px, 4vmin, 28px)',
                            animation: 'invGlint 2.6s ease-in-out infinite',
                        }}>{w.emoji || '🕵️'}</span>
                        <span style={{
                            background: 'rgba(0,0,0,0.72)', color: INK, borderRadius: 6, padding: '2px 7px',
                            fontSize: 'clamp(9px, 1.9vmin, 12px)', fontWeight: 700, whiteSpace: 'nowrap',
                            // Hotspots sit as far right as x=86% and names run to 40
                            // chars, so an unbounded nowrap label gets sheared off by
                            // the scene's overflow:hidden. Ellipsize like the clue hint;
                            // cap by viewport too so a 320px screen can't be out-labeled.
                            maxWidth: 'min(140px, 42vw)', overflow: 'hidden', textOverflow: 'ellipsis',
                            ...edgeLabelShift(w.x),
                        }}>{w.name}{w.questions_left === 0 ? ' 🤐' : ''}</span>
                    </button>
                ))}
                {clues.map(c => {
                    const bagged = c.found || !!localFound[c.id];
                    return (
                        <button key={c.id} onClick={() => { setFocusWitness(null); inspectClue(c); }}
                            draggable={false} onDragStart={e => e.preventDefault()}
                            style={{ ...hotspotBase, left: `${c.x}%`, top: `${c.y}%`, opacity: bagged ? 0.55 : 1 }}>
                            <span style={{
                                width: 'clamp(34px, 6.5vmin, 46px)', height: 'clamp(34px, 6.5vmin, 46px)', borderRadius: '50%',
                                background: 'rgba(10,10,16,0.66)', border: `2px dashed ${bagged ? GREEN : 'rgba(217,186,114,0.7)'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 'clamp(15px, 3vmin, 21px)',
                                animation: bagged ? 'none' : 'invGlint 2s ease-in-out infinite',
                            }}>{bagged ? '✓' : (c.emoji || '🔍')}</span>
                            {!bagged && c.hint && !narrowStage && (
                                <span style={{
                                    background: 'rgba(0,0,0,0.6)', color: INK_DIM, borderRadius: 6, padding: '1px 6px',
                                    fontSize: 'clamp(8px, 1.7vmin, 11px)', whiteSpace: 'nowrap',
                                    maxWidth: 'min(140px, 42vw)',
                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                    ...edgeLabelShift(c.x),
                                }}>{c.hint}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* 🗂 Docked case file (Nina 07-26): default-open right sidebar on desktop,
                same component + storage keys as the modal so notes/seen-state carry.
                Collapse via the header ✕ or the top-strip CASE FILE toggle. */}
            {sidebarOpen && (
                <div style={{
                    width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0,
                    background: 'linear-gradient(180deg, #eed9a4 0%, #dcc07c 100%)',
                    textShadow: 'none', // 🗒️ paper resets the halo (roomHaloPaper.test.js)
                    borderLeft: '1px solid #b28f4a', boxShadow: '-6px 0 18px rgba(0,0,0,0.45)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px 6px' }}>
                        <span style={{ fontSize: '0.72em', fontWeight: 800, letterSpacing: '0.14em', color: '#4a3a17' }}>
                            🗂 CASE FILE · THE INVESTIGATION
                        </span>
                        <div style={{ flex: 1 }} />
                        <button onClick={() => setFileDocked(false)} aria-label="Collapse case file"
                            style={{ background: 'none', border: 'none', color: '#4a3a17', fontWeight: 800, cursor: 'pointer', fontSize: '0.9em', padding: '2px 6px' }}
                        >✕</button>
                    </div>
                    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 8px 8px' }}>
                        <WhodunnitCaseFile {...caseFileProps} visible={true} />
                    </div>
                </div>
            )}
            </div>

            {/* Error flash */}
            {errorFlash && (
                <div style={{
                    position: 'absolute', top: 64, left: '50%', transform: 'translateX(-50%)', zIndex: 6,
                    background: 'rgba(127,29,29,0.95)', color: '#fff', padding: '7px 14px', borderRadius: 8,
                    fontWeight: 700, fontSize: 'clamp(11px, 2.2vmin, 14px)', maxWidth: '86%',
                }}>{errorFlash}</div>
            )}

            {/* Bottom dock strip: ready state. Manila desk band (Nina 07-25:
                the dark strip read as empty floor and got missed) — same paper
                chrome as the case file, so the bottom edge reads as "the desk"
                where your next move lives. Button inverts to dark-on-paper. */}
            <div style={{
                // Column + centered + real bottom breathing room (Nina 07-26:
                // the button read as glued to the screen edge)
                position: 'relative', zIndex: 4, display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 10, textAlign: 'center',
                padding: '14px 12px max(20px, env(safe-area-inset-bottom))',
                background: 'linear-gradient(180deg, #eed9a4, #d9ba72)',
                textShadow: 'none', // 🗒️ paper resets the halo (roomHaloPaper.test.js)
                borderTop: '2px solid #b28f4a',
                boxShadow: '0 -6px 18px rgba(0,0,0,0.45)',
            }}>
                {youReady ? (
                    <div style={{ color: '#2e6b30', fontWeight: 800, fontSize: 'clamp(12px, 2.4vmin, 15px)' }}>
                        ✓ Ready. Waiting on the others. Read your file.
                    </div>
                ) : (
                    <>
                        <div style={{ color: '#4a3a17', fontWeight: 700, fontSize: 'clamp(11px, 2.2vmin, 14px)' }}>
                            {/* The killer isn't investigating, they're covering
                                (user 07-16: killer needs escape-POV copy) */}
                            {caseFileProps?.youDidIt
                                ? '🔪 You know what happened. Find out what THEY know. Build your way out.'
                                : 'Question witnesses. Bag evidence. Get your story straight.'}
                        </div>
                        {!isSpectator && (
                            /* Pulsing glow + bigger hit (user 07-16: button got lost
                               against the noir dock) */
                            <button onClick={sendReady} style={{
                                background: '#1c1710', color: MANILA, border: '2px solid #4a3a17', borderRadius: 12,
                                padding: '13px 30px', fontWeight: 900, letterSpacing: '0.08em', cursor: 'pointer',
                                fontSize: 'clamp(14px, 2.8vmin, 17px)',
                                animation: 'invReadyPulse 2.2s ease-in-out infinite',
                            }}>🕵️ I'M READY</button>
                        )}
                    </>
                )}
            </div>

            {/* Witness thread — bottom sheet */}
            {activeWitness && (
                <div style={{
                    // Centered over the SCENE, not the full overlay (the docked file
                    // shifts the scene's visual center left by its 320px width).
                    position: 'absolute', left: sidebarOpen ? 'calc((100% - 320px) / 2)' : '50%',
                    bottom: 0,
                    // translateY rides the keyboard up (see kbLift) so the input +
                    // ASK stay on screen; maxHeight yields the same amount so the
                    // sheet doesn't get pushed off the top of the scene.
                    transform: `translateX(-50%) translateY(-${kbLift}px)`,
                    width: sidebarOpen ? 'min(560px, calc(100% - 320px))' : 'min(560px, 100%)',
                    maxHeight: kbLift > 0 ? `calc(64% - ${Math.round(kbLift * 0.35)}px)` : '64%', zIndex: 5,
                    background: NOIR_BG, borderRadius: '14px 14px 0 0', border: `1px solid ${MANILA}44`, borderBottom: 'none',
                    display: 'flex', flexDirection: 'column', animation: 'invSlideUp 0.22s ease-out',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px 8px' }}>
                        <span style={{ fontSize: 24 }}>{activeWitness.emoji || '🕵️'}</span>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ color: MANILA, fontWeight: 900, fontSize: 'clamp(13px, 2.6vmin, 16px)' }}>{activeWitness.name}</div>
                            {activeWitness.look && <div style={{ color: INK_DIM, fontSize: 'clamp(10px, 2vmin, 12px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeWitness.look}</div>}
                        </div>
                        <button onClick={() => setFocusWitness(null)} style={{
                            marginLeft: 'auto', background: 'none', border: 'none', color: INK_DIM,
                            fontSize: 20, cursor: 'pointer', padding: 4,
                        }}>✕</button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '4px 12px', minHeight: 90 }}>
                        {thread.length === 0 && !pendingQ && (
                            <div style={{ color: INK_DIM, fontSize: 'clamp(11px, 2.2vmin, 13px)', padding: '10px 0' }}>
                                They were there. Ask sharp. {questionsLeft} questions.
                            </div>
                        )}
                        {thread.map((t, i) => (
                            <div key={i} style={{ marginBottom: 9 }}>
                                <div style={{ textAlign: 'right' }}>
                                    <span style={{
                                        display: 'inline-block', background: 'rgba(217,186,114,0.16)', color: INK,
                                        borderRadius: '10px 10px 2px 10px', padding: '6px 10px', maxWidth: '85%',
                                        fontSize: 'clamp(12px, 2.4vmin, 14px)', textAlign: 'left',
                                    }}>{t.q}</span>
                                </div>
                                <div style={{ marginTop: 4 }}>
                                    <span style={{
                                        display: 'inline-block', background: 'rgba(255,255,255,0.07)', color: INK,
                                        borderLeft: `3px solid ${MANILA}`, borderRadius: '2px 10px 10px 10px',
                                        padding: '6px 10px', maxWidth: '85%', fontSize: 'clamp(12px, 2.4vmin, 14px)',
                                        fontStyle: 'italic',
                                    }}>{t.a}</span>
                                </div>
                            </div>
                        ))}
                        {pendingQ && (
                            <div style={{ marginBottom: 9 }}>
                                <div style={{ textAlign: 'right' }}>
                                    <span style={{
                                        display: 'inline-block', background: 'rgba(217,186,114,0.16)', color: INK,
                                        borderRadius: '10px 10px 2px 10px', padding: '6px 10px', maxWidth: '85%',
                                        fontSize: 'clamp(12px, 2.4vmin, 14px)', textAlign: 'left',
                                    }}>{pendingQ.q}</span>
                                </div>
                                <div style={{ marginTop: 4, color: INK_DIM, fontStyle: 'italic', fontSize: 'clamp(11px, 2.2vmin, 13px)' }}>
                                    {activeWitness.name} eyes you...
                                </div>
                            </div>
                        )}
                        <div ref={threadEndRef} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, padding: '8px 12px max(10px, env(safe-area-inset-bottom))', alignItems: 'center' }}>
                        {isSpectator ? (
                            <div style={{ color: INK_DIM, fontSize: 'clamp(11px, 2.2vmin, 13px)' }}>Spectators watch. Suspects ask.</div>
                        ) : questionsLeft <= 0 && !pendingQ ? (
                            <div style={{ color: INK_DIM, fontSize: 'clamp(11px, 2.2vmin, 13px)' }}>
                                🤐 That's all {activeWitness.name} will say this round.
                            </div>
                        ) : outOfMoves && !pendingQ ? (
                            <div style={{ color: INK_DIM, fontSize: 'clamp(11px, 2.2vmin, 13px)' }}>
                                🥾 You're out of moves tonight. Make your read.
                            </div>
                        ) : (
                            <>
                                <input
                                    value={question}
                                    onChange={e => setQuestion(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') askWitness(); }}
                                    placeholder={`Ask ${activeWitness.name}...`}
                                    maxLength={280}
                                    disabled={!!pendingQ}
                                    style={{
                                        flex: 1, minWidth: 0, background: 'rgba(255,255,255,0.07)', color: INK,
                                        border: `1px solid ${MANILA}44`, borderRadius: 9, padding: '9px 11px',
                                        // 16px FLOOR: under 16 iOS Safari zooms the page on focus, and
                                        // inside a position:fixed takeover there's no pinch-out back.
                                        // Same rule as WatchPartyTheater's composer.
                                        fontSize: 'clamp(16px, 2.6vmin, 17px)', outline: 'none',
                                    }}
                                />
                                <button onClick={askWitness} disabled={!!pendingQ || !question.trim()} style={{
                                    background: MANILA, color: '#151009', border: 'none', borderRadius: 9,
                                    padding: '9px 14px', fontWeight: 900, cursor: 'pointer', whiteSpace: 'nowrap',
                                    opacity: (!!pendingQ || !question.trim()) ? 0.5 : 1,
                                    fontSize: 'clamp(12px, 2.4vmin, 14px)',
                                }}>ASK</button>
                                <span style={{ color: questionsLeft <= 1 ? RED : INK_DIM, fontWeight: 800, fontSize: 'clamp(10px, 2vmin, 12px)', whiteSpace: 'nowrap' }}>
                                    {questionsLeft} left
                                </span>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Case file — the stage's own manila drawer (same component as the chat
                panel's, same localStorage keys, so notes/seen-state carry over) */}
            {showFile && caseFileProps && (
                <div onClick={() => setShowFile(false)} style={{
                    position: 'absolute', inset: 0, zIndex: 6, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.55)',
                }}>
                    <div onClick={e => e.stopPropagation()} style={{
                        width: 'min(480px, 92%)', maxHeight: '78%', display: 'flex', flexDirection: 'column',
                        background: 'linear-gradient(180deg, #eed9a4 0%, #dcc07c 100%)',
                        textShadow: 'none', // 🗒️ paper resets the halo (roomHaloPaper.test.js)
                        border: '1px solid #b28f4a', borderRadius: 12,
                        boxShadow: '0 10px 28px rgba(0,0,0,0.5)', animation: 'invSlideUp 0.2s ease-out',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px 6px' }}>
                            <span style={{ fontSize: '0.72em', fontWeight: 800, letterSpacing: '0.14em', color: '#4a3a17' }}>
                                🗂 CASE FILE · THE INVESTIGATION
                            </span>
                            <div style={{ flex: 1 }} />
                            <button onClick={() => setShowFile(false)} aria-label="Close case file"
                                style={{ background: 'none', border: 'none', color: '#4a3a17', fontWeight: 800, cursor: 'pointer', fontSize: '0.9em', padding: '2px 6px' }}
                            >✕</button>
                        </div>
                        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 8px 8px' }}>
                            <WhodunnitCaseFile {...caseFileProps} visible={showFile} />
                        </div>
                    </div>
                </div>
            )}

            {/* Clue inspect modal — a fresh bag plays its mini-game where the old
                "Dusting it off..." wait sat; the API line landing mid-game holds
                for the finish, then the shutter fires AT the filing beat. */}
            {focusClue && (
                <div onClick={() => setFocusClue(null)} style={{
                    position: 'absolute', inset: 0, zIndex: 5, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.55)',
                }}>
                    <div onClick={e => e.stopPropagation()} style={{
                        width: 'min(420px, 88%)', background: NOIR_BG, border: `1px solid ${MANILA}66`,
                        borderRadius: 14, padding: '18px 18px 16px', textAlign: 'center',
                        animation: 'invSlideUp 0.2s ease-out', position: 'relative', overflow: 'hidden',
                    }}>
                        {clueRevealed && focusClue.fresh && (
                            <div style={{
                                position: 'absolute', inset: 0, background: '#fff',
                                animation: 'invShutter 0.65s ease-out forwards', pointerEvents: 'none',
                            }} />
                        )}
                        <div style={{ fontSize: 40 }}>{focusClue.emoji || '🔍'}</div>
                        <div style={{ color: MANILA, fontWeight: 900, letterSpacing: '0.08em', marginTop: 4, fontSize: 'clamp(14px, 2.8vmin, 17px)' }}>
                            {focusClue.object}
                        </div>
                        {focusClue.hint && (
                            <div style={{ color: INK_DIM, fontSize: 'clamp(10px, 2vmin, 12px)', marginTop: 2 }}>Found {focusClue.hint}</div>
                        )}
                        <div style={{ marginTop: 12, minHeight: 44 }}>
                            {clueRevealed && focusClue.evidence_line ? (
                                <>
                                    <div style={{ color: INK, fontSize: 'clamp(13px, 2.6vmin, 15px)', lineHeight: 1.5 }}>
                                        {focusClue.evidence_line}
                                    </div>
                                    <div style={{
                                        marginTop: 10, display: 'inline-block', color: GREEN, fontWeight: 900,
                                        border: `1.5px solid ${GREEN}`, borderRadius: 6, padding: '3px 9px',
                                        fontSize: 'clamp(10px, 2vmin, 12px)', letterSpacing: '0.1em', transform: 'rotate(-3deg)',
                                    }}>📸 FILED TO CASE FILE</div>
                                </>
                            ) : !minigameDone ? (
                                <EvidenceMinigame
                                    clueId={focusClue.id}
                                    emoji={focusClue.emoji}
                                    onComplete={() => setMinigameDone(true)}
                                />
                            ) : (
                                <div style={{ color: INK_DIM, fontStyle: 'italic', fontSize: 'clamp(12px, 2.4vmin, 14px)' }}>Bagging it...</div>
                            )}
                        </div>
                        <button onClick={() => setFocusClue(null)} style={{
                            marginTop: 12, background: 'rgba(217,186,114,0.14)', color: MANILA,
                            border: `1px solid ${MANILA}55`, borderRadius: 8, padding: '7px 18px',
                            fontWeight: 800, cursor: 'pointer', fontSize: 'clamp(12px, 2.4vmin, 14px)',
                        }}>KEEP LOOKING</button>
                    </div>
                </div>
            )}

            {/* 📸→🗂 Polaroid in flight: fresh evidence flying home to the CASE
                FILE corner. Over everything (z7), never interactive, one per clue. */}
            {bagFlight && (
                <div key={bagFlight.key} onAnimationEnd={() => setBagFlight(null)} style={{
                    position: 'absolute', zIndex: 7, pointerEvents: 'none',
                    left: '50%', top: '42%',
                    background: '#f5f0e6', padding: '5px 5px 14px', borderRadius: 3,
                    textShadow: 'none', // 🗒️ paper resets the halo (roomHaloPaper.test.js)
                    boxShadow: '0 6px 18px rgba(0,0,0,0.5)',
                    animation: 'invPolaroidFly 0.95s cubic-bezier(0.5, -0.05, 0.7, 1) forwards',
                }}>
                    <div style={{
                        width: 54, height: 44, background: '#1a1712',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
                    }}>{bagFlight.emoji}</div>
                </div>
            )}
        </div>
    );
}
